/**
 * ReviewReturnDialog — capture an optional revision note before
 * returning an asset to draft. Small modal, escape closes, Enter
 * confirms. Deliberate — no auto-submit on typing.
 */
import { useEffect, useRef, useState } from "react";

export function ReviewReturnDialog({
  open,
  onCancel,
  onConfirm,
  isPending,
}: {
  open: boolean;
  onCancel: () => void;
  onConfirm: (note: string) => void;
  isPending: boolean;
}): JSX.Element | null {
  const [note, setNote] = useState("");
  const ref = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (open) {
      setNote("");
      // Focus the note field on open for keyboard-first founders.
      requestAnimationFrame(() => ref.current?.focus());
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onCancel();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onCancel]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-end md:items-center justify-center bg-black/60 p-4"
      data-testid="review-return-dialog-backdrop"
      onClick={onCancel}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Return for revision"
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md rounded-2xl border border-white/10 bg-[color:var(--brand-charcoal,#12151C)] p-5 shadow-2xl"
        data-testid="review-return-dialog"
      >
        <h3 className="text-lg font-semibold text-white">
          Return for revision
        </h3>
        <p className="mt-1 text-sm text-white/60">
          Optional — tell the department what to change. This asset will move
          back to draft.
        </p>
        <textarea
          ref={ref}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="e.g. Punch up the opening line."
          className="mt-4 min-h-[100px] w-full resize-y rounded-lg border border-white/10 bg-black/30 p-3 text-sm text-white/90 placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-cyan-400/60"
          data-testid="review-return-dialog-note"
          disabled={isPending}
        />
        <div className="mt-4 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={isPending}
            className="rounded-full border border-white/15 bg-transparent px-4 py-2 text-sm text-white/80 hover:bg-white/5 disabled:opacity-50"
            data-testid="review-return-dialog-cancel"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => onConfirm(note.trim())}
            disabled={isPending}
            className="rounded-full bg-white px-4 py-2 text-sm font-semibold text-slate-950 hover:brightness-95 disabled:opacity-60"
            data-testid="review-return-dialog-confirm"
          >
            {isPending ? "Returning…" : "Return for revision"}
          </button>
        </div>
      </div>
    </div>
  );
}
